import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const LocationCard = ({ location, participants, isSelected, onSelect }) => {
  const getTransportIcon = (method) => {
    const icons = {
      driving: 'Car',
      walking: 'PersonStanding',
      'public-transit': 'Bus',
      bike: 'Bike'
    };
    return icons?.[method] || 'MapPin';
  };

  const formatDuration = (minutes) => {
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  const maxTravelTime = Math.max(...location?.travelTimes?.map(t => t?.duration));
  const minTravelTime = Math.min(...location?.travelTimes?.map(t => t?.duration));
  const timeDifference = maxTravelTime - minTravelTime;

  return (
    <div 
      className={`
        bg-card border rounded-lg overflow-hidden transition-smooth hover-lift
        ${isSelected ? 'border-primary shadow-lg ring-3 ring-primary/20' : 'border-border shadow-md'}
      `}
    >
      <div className="relative h-40 md:h-48 lg:h-56 overflow-hidden">
        <Image
          src={location?.image}
          alt={location?.imageAlt}
          className="w-full h-full object-cover"
        />
        {timeDifference <= 5 && (
          <div className="absolute top-3 right-3 bg-success text-success-foreground px-3 py-1 rounded-full flex items-center gap-1.5 shadow-md">
            <Icon name="CheckCircle" size={14} />
            <span className="caption font-medium">Fair Times</span>
          </div>
        )}
      </div>
      <div className="p-4 md:p-5 lg:p-6">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex-1 min-w-0">
            <h3 className="font-heading text-base md:text-lg lg:text-xl font-semibold text-foreground mb-1 line-clamp-1">
              {location?.name}
            </h3>
            <p className="caption text-muted-foreground line-clamp-2 mb-2">
              {location?.address}
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded-md caption font-medium">
                <Icon name="MapPin" size={12} />
                {location?.type}
              </span>
              {location?.rating && (
                <span className="inline-flex items-center gap-1 caption text-muted-foreground">
                  <Icon name="Star" size={12} className="text-accent fill-accent" />
                  {location?.rating}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-2 mb-4">
          <div className="flex items-center justify-between pb-2 border-b border-border">
            <span className="caption font-medium text-foreground">Participant</span>
            <span className="caption font-medium text-foreground">Travel Time</span>
          </div>
          {location?.travelTimes?.map((travel, index) => {
            const participant = participants?.find(p => p?.id === travel?.participantId);
            return (
              <div key={travel?.participantId} className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Icon name={getTransportIcon(participant?.transportMethod)} size={16} className="text-primary" />
                  </div>
                  <span className="caption text-foreground truncate">{participant?.name || `Participant ${index + 1}`}</span>
                </div>
                <span className="data-text text-sm font-medium text-foreground whitespace-nowrap">
                  {formatDuration(travel?.duration)}
                </span>
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-between pt-3 border-t border-border mb-4">
          <span className="text-sm font-medium text-foreground">Average Travel Time</span>
          <span className="data-text text-base font-semibold text-primary">
            {formatDuration(Math.round(location?.travelTimes?.reduce((sum, t) => sum + t?.duration, 0) / location?.travelTimes?.length))}
          </span>
        </div>

        <Button
          variant={isSelected ? "default" : "outline"}
          fullWidth
          iconName={isSelected ? "Check" : "MapPin"}
          iconPosition="left"
          onClick={() => onSelect(location?.id)}
        >
          {isSelected ? 'Selected' : 'Select Location'}
        </Button>
      </div>
    </div>
  );
};

export default LocationCard;